import { useEffect, useState } from "react";
import { useStartGuestSession } from "@/hooks/use-orders";

export type GuestSessionState = {
  loading: boolean;
  tableLabel: string | null;
  error: string | null;
};

/**
 * Bootstraps a guest session when a `?t=<qrToken>` param is present.
 * Idempotent: re-running with the same token re-issues the cookie.
 */
export function useGuestSession(orgSlug: string | undefined, qrToken: string | null) {
  const start = useStartGuestSession();
  const [state, setState] = useState<GuestSessionState>({
    loading: false,
    tableLabel: null,
    error: null,
  });

  useEffect(() => {
    if (!(orgSlug && qrToken)) {
      setState({ loading: false, tableLabel: null, error: null });
      return;
    }

    let cancelled = false;
    setState({ loading: true, tableLabel: null, error: null });

    start.mutateAsync({ orgSlug, qrToken }).then(
      (result) => {
        if (cancelled) {
          return;
        }
        setState({
          loading: false,
          tableLabel: result.tableLabel,
          error: null,
        });
      },
      (error: unknown) => {
        if (cancelled) {
          return;
        }
        setState({
          loading: false,
          tableLabel: null,
          error: error instanceof Error ? error.message : "Could not start session",
        });
      },
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSlug, qrToken]);

  return state;
}
